/**
 * The busy-week calendar (approved design C1).
 *
 * A crammed Monday-to-Friday week sits still while short lesson slots drop
 * into the gaps that already exist, one after another, on a loop. Each slot's
 * height is scaled to its length, so a 10-minute lesson reads bigger than a
 * 5-minute one.
 */

interface Busy {
  top: number;
  height: number;
  label: string;
}

interface Slot {
  top: number;
  minutes: number;
  /** Animation offset so the slots land one after another. */
  delay: string;
}

interface Day {
  name: string;
  busy: Busy[];
  slot: Slot;
}

/** 1 minute of a lesson ≈ 1.8px, floored so the shortest slot stays legible. */
const slotHeight = (minutes: number) => Math.max(11, Math.round(minutes * 1.8));
const slotFontSize = (minutes: number) => (minutes >= 10 ? 8.5 : minutes >= 8 ? 8 : 7.5);

const DAYS: Day[] = [
  {
    name: "MON",
    busy: [
      { top: 4, height: 28, label: "Standup" },
      { top: 40, height: 62, label: "Deep work" },
      { top: 128, height: 62, label: "Client call" },
      { top: 196, height: 42, label: "Lunch" },
      { top: 244, height: 56, label: "Reports" },
    ],
    slot: { top: 107, minutes: 8, delay: "0.6s" },
  },
  {
    name: "TUE",
    busy: [
      { top: 4, height: 28, label: "Standup" },
      { top: 38, height: 96, label: "Workshop" },
      { top: 160, height: 42, label: "Lunch" },
      { top: 228, height: 70, label: "1:1s" },
    ],
    slot: { top: 209, minutes: 6, delay: "1.5s" },
  },
  {
    name: "WED",
    busy: [
      { top: 4, height: 28, label: "Standup" },
      { top: 40, height: 56, label: "Planning" },
      { top: 120, height: 42, label: "Lunch" },
      { top: 168, height: 90, label: "Client visit" },
    ],
    slot: { top: 264, minutes: 10, delay: "2.4s" },
  },
  {
    name: "THU",
    busy: [
      { top: 4, height: 28, label: "Standup" },
      { top: 56, height: 80, label: "Deep work" },
      { top: 142, height: 42, label: "Lunch" },
      { top: 190, height: 62, label: "Interviews" },
      { top: 258, height: 42, label: "Review" },
    ],
    slot: { top: 40, minutes: 5, delay: "3.3s" },
  },
  {
    name: "FRI",
    busy: [
      { top: 4, height: 28, label: "Standup" },
      { top: 40, height: 62, label: "Client call" },
      { top: 108, height: 42, label: "Lunch" },
      { top: 156, height: 56, label: "Demos" },
      { top: 240, height: 58, label: "Retro" },
    ],
    slot: { top: 219, minutes: 6, delay: "4.2s" },
  },
];

const HOURS = ["9:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export const TOTAL_LESSON_MINUTES = DAYS.reduce((sum, day) => sum + day.slot.minutes, 0);
export const TOTAL_LESSONS = DAYS.length;

export function BusyCalendar() {
  return (
    <div
      className="cal"
      role="img"
      aria-label={`A full working week where ${TOTAL_LESSONS} short lessons totalling ${TOTAL_LESSON_MINUTES} minutes fit into the gaps between existing meetings.`}
    >
      <div className="cal-head">
        <div />
        {DAYS.map((day) => (
          <div key={day.name}>{day.name}</div>
        ))}
      </div>
      <div className="cal-body">
        <div className="cal-times">
          {HOURS.map((hour) => (
            <span key={hour}>{hour}</span>
          ))}
        </div>
        {DAYS.map((day) => (
          <div className="cal-day" key={day.name}>
            {day.busy.map((block) => (
              <div
                className="busy"
                key={`${day.name}-${block.top}`}
                style={{ top: `${block.top}px`, height: `${block.height}px` }}
              >
                {block.label}
              </div>
            ))}
            <div
              className="cal-slot"
              style={{
                top: `${day.slot.top}px`,
                height: `${slotHeight(day.slot.minutes)}px`,
                fontSize: `${slotFontSize(day.slot.minutes)}px`,
                animationDelay: day.slot.delay,
              }}
            >
              {day.slot.minutes} MIN
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
